import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Phone, Mail, MapPin, Briefcase, Star, MessageCircle, FileText, Download, Loader2, Calendar, Clock, Shield, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import ScheduleInterviewModal from './ScheduleInterviewModal';
import PesoBadge from './PesoBadge';
import { format } from 'date-fns';
import { toManilaDate } from '../lib/dateUtils';
import { missingDocuments, neededToApply } from '../lib/jobDocuments';

const ApplicantDetailModal = ({ open, onOpenChange, applicant, job }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [certifications, setCertifications] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadingCert, setDownloadingCert] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [existingInterview, setExistingInterview] = useState(null);
  const [checkingInterview, setCheckingInterview] = useState(false);

  // Get seeker/contractor before early return to avoid hooks order violation
  const seeker = applicant?.seeker || applicant?.contractor;
  // Documents this job asks for that the applicant hasn't sent (e.g. "if hired" clearances they'll
  // bring later). Only for a real application on a job whose document list we have.
  const notYetSubmitted = applicant?.id && Array.isArray(job?.required_documents)
    ? missingDocuments(job.required_documents, applicant.documents) : [];
  const askForDocuments = () => {
    const firstName = String(seeker?.name || '').trim().split(/\s+/)[0] || '';
    const draft = t('jobDocs.askDraft', 'Hi {{name}}, thanks for applying for {{job}}. When you can, please send your {{docs}} here.', {
      name: firstName, job: job?.title || '', docs: notYetSubmitted.map((d) => d.name).join(', '),
      interpolation: { escapeValue: false },
    });
    // Passed in router state (not the URL) so an outside link can't pre-fill someone's message box.
    navigate(`/messages?user=${encodeURIComponent(seeker?.id || '')}`, { state: { draft: draft.slice(0, 500) } });
    onOpenChange(false);
  };
  const jobId = job?.id || applicant?.job_id || applicant?.gig_id;

  // Check for existing interview when modal opens
  useEffect(() => {
    if (!open || !seeker?.id || !jobId) {
      setExistingInterview(null);
      return;
    }
    checkExistingInterview();
  }, [open, seeker?.id, jobId]);

  // Fetch certifications when modal opens
  useEffect(() => {
    if (!open || !seeker?.id) return;
    fetchCertifications();
  }, [open, seeker?.id]);

  const checkExistingInterview = async () => {
    setCheckingInterview(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      // Check for any active interview (pending, confirmed, rescheduled) for this applicant+job
      const response = await api.get('/api/interviews', {
        params: { job_id: jobId },
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        // Find interview for this specific applicant
        const interview = response.data.interviews.find(
          i => i.applicant_id === seeker.id &&
               ['pending', 'confirmed', 'rescheduled'].includes(i.status)
        );
        setExistingInterview(interview || null);
      }
    } catch (error) {
      console.error('Error checking existing interview:', error);
    } finally {
      setCheckingInterview(false);
    }
  };

  // Early return AFTER all hooks to maintain hooks order
  if (!applicant) return null;

  const fetchCertifications = async () => {
    setLoadingCerts(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get(`/api/certifications/user/${seeker.id}`, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      setCertifications(response.data.certifications || []);
    } catch (error) {
      console.error('Failed to fetch certifications:', error);
    } finally {
      setLoadingCerts(false);
    }
  };

  const handleDownloadDocument = async (index) => {
    if (!applicant?.id) return;

    setDownloadingDoc(index);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to download documents');
        return;
      }

      // Use the secure download endpoint
      const response = await api.get(`/api/applications/${applicant.id}/document/${index}`, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        },
        responseType: 'blob' // Important: receive as blob for file download
      });

      // Create download link
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;

        // Get filename from Content-Disposition header or use document name
        const contentDisposition = response.headers['content-disposition'];
        let filename = applicant.documents[index]?.name || 'document';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/"/g, '');
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Document downloaded');
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const handleDownloadCertification = async (cert) => {
    if (!cert?.id) {
      toast.error('Invalid certification');
      return;
    }

    setDownloadingCert(cert.id);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to download certifications');
        return;
      }

      // Use the secure download endpoint
      const response = await api.get(`/api/certifications/${cert.id}/download`, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use cert title
      const contentDisposition = response.headers['content-disposition'];
      let filename = cert.title || 'certification';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Certification downloaded');
    } catch (error) {
      console.error('Failed to download certification:', error);
      toast.error('Failed to download certification');
    } finally {
      setDownloadingCert(null);
    }
  };

  const getMatchColor = (score) => {
    if (score >= 75) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('applicationModal.applicantProfile')}</DialogTitle>
          <DialogDescription className="sr-only">
            View applicant details and take actions like scheduling interviews
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4">
            <Avatar className="w-20 h-20">
              {seeker?.avatar_url && (
                <AvatarImage
                  src={seeker.avatar_url}
                  alt={seeker?.name || 'User'}
                />
              )}
              <AvatarFallback>
                {seeker?.name?.[0] || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-heading text-xl font-bold mb-1">
                {seeker?.name}
              </h3>
              <div className="flex flex-wrap gap-2">
                {seeker?.verified && (
                  <Badge className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold shadow-md border-teal-400 border">
                    <Shield size={14} className="mr-1" />
                    True Boholano
                  </Badge>
                )}
                {seeker?.rating > 0 && (
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                    <Star size={12} className="mr-1 fill-yellow-500" />
                    {seeker.rating.toFixed(1)} {t('applicationModal.rating')}
                  </Badge>
                )}
                {seeker?.peso_office && (
                  <PesoBadge officeName={seeker.peso_office_name} color={seeker.peso_office_color} />
                )}
              </div>
            </div>
          </div>

          {/* AI Match Score */}
          {applicant?.match_score != null && (
            <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-primary" />
                  <span className="font-semibold text-sm">AI Match Score</span>
                </div>
                <Badge className={`${getMatchColor(applicant.match_score)} text-white`}>
                  {applicant.match_score}%
                </Badge>
              </div>
              {(applicant?.match_reason || applicant?.match_reason_ceb) && (
                <p className="text-sm text-muted-foreground mt-2 italic">
                  {i18n.language === 'ceb' && applicant.match_reason_ceb
                    ? applicant.match_reason_ceb
                    : applicant.match_reason}
                </p>
              )}
            </div>
          )}

          {/* Contact Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold mb-3">{t('applicationModal.contactInfo')}</h4>
            <div className="flex items-center gap-3 text-sm">
              <Phone size={16} className="text-muted-foreground" />
              <span>{seeker?.phone || t('applicationModal.notProvided')}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Mail size={16} className="text-muted-foreground shrink-0" />
              <span className="min-w-0 break-all">{seeker?.email || t('applicationModal.notProvided')}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <MapPin size={16} className="text-muted-foreground" />
              <span>{seeker?.address || seeker?.town || t('applicationModal.notProvided')}</span>
            </div>
          </div>

          {/* Skills */}
          {seeker?.skills && seeker.skills.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3">{t('applicationModal.skills')}</h4>
              <div className="flex flex-wrap gap-2">
                {seeker.skills.map((skill, idx) => (
                  <Badge key={idx} variant="secondary">{skill}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Experience/Bio */}
          {seeker?.experience && (
            <div>
              <h4 className="font-semibold mb-3">{t('applicationModal.experience')}</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-line">
                {seeker.experience}
              </p>
            </div>
          )}

          {/* Cover Letter */}
          {applicant.cover && (
            <div>
              <h4 className="font-semibold mb-3">{t('applicationModal.coverLetter')}</h4>
              <div className="bg-primary/5 rounded-lg p-4">
                <p className="text-sm whitespace-pre-line">{applicant.cover}</p>
              </div>
            </div>
          )}

          {/* Custom Question Answer */}
          {applicant.custom_answer && applicant.custom_answer !== '__SUBMITTED__' && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                {t('applicationModal.screeningAnswer', 'Screening Question Answer')}
                <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">Submitted</Badge>
              </h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm whitespace-pre-line text-blue-800">{applicant.custom_answer}</p>
              </div>
            </div>
          )}

          {/* Application Documents */}
          {applicant.documents && applicant.documents.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                {t('applicationModal.uploadedDocuments', 'Uploaded Documents')}
                <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                  {applicant.documents.length} {applicant.documents.length === 1 ? 'file' : 'files'}
                </Badge>
              </h4>
              <div className="space-y-2">
                {applicant.documents.map((doc, index) => (
                  <button
                    key={index}
                    onClick={() => handleDownloadDocument(index)}
                    disabled={downloadingDoc === index}
                    className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left disabled:opacity-50"
                  >
                    <FileText size={18} className="text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">Click to download securely</p>
                    </div>
                    {downloadingDoc === index ? (
                      <Loader2 size={16} className="text-primary flex-shrink-0 animate-spin" />
                    ) : (
                      <Download size={16} className="text-primary flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Requested documents the applicant hasn't sent yet */}
          {notYetSubmitted.length > 0 && seeker?.id && (
            <div data-testid="applicant-missing-documents">
              <h4 className="font-semibold mb-3 flex flex-wrap items-center gap-2">
                {t('jobDocs.notYetSubmitted', 'Not yet submitted')}
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-xs">
                  {notYetSubmitted.length}
                </Badge>
              </h4>
              <ul className="space-y-1.5 mb-3">
                {notYetSubmitted.map((doc, i) => (
                  <li key={`${doc.name}-${i}`} className="flex flex-wrap items-center gap-2 text-sm p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <FileText size={16} className="text-amber-700 shrink-0" />
                    <span className="flex-1 min-w-0 break-words">{doc.name}</span>
                    <span className="text-xs text-amber-800">
                      {neededToApply(doc) ? t('jobDocs.toApply', 'To apply') : t('jobDocs.ifHired', 'If hired')}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="w-full rounded-xl min-h-[44px]"
                onClick={askForDocuments}
                data-testid="ask-for-documents-button"
              >
                <MessageCircle size={16} className="mr-2" />
                {t('jobDocs.askInChat', 'Ask for them in chat')}
              </Button>
            </div>
          )}

          {/* Certifications & Licenses */}
          {certifications.length > 0 && (
            <div data-testid="applicant-certifications-section">
              <h4 className="font-semibold mb-3">{t('applicationModal.certificationsLicenses')}</h4>
              <div className="space-y-2">
                {certifications.map((cert) => (
                  <Card
                    key={cert.id}
                    className="border"
                    data-testid={`applicant-cert-${cert.id}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText size={14} className="text-primary flex-shrink-0" />
                            <h5 className="font-medium text-sm truncate">{cert.title}</h5>
                            {cert.expiry_date ? (
                              isExpired(cert.expiry_date) ? (
                                <Badge variant="destructive" className="text-xs">Expired</Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-success/10 text-success border-success/20 text-xs">
                                  Valid
                                </Badge>
                              )
                            ) : (
                              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 text-xs">
                                No Expiry
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>Issued: {formatDate(cert.issue_date)}</div>
                            {cert.expiry_date && <div>Expires: {formatDate(cert.expiry_date)}</div>}
                          </div>
                        </div>
                        {/* Download button */}
                        <button
                          onClick={() => handleDownloadCertification(cert)}
                          disabled={downloadingCert === cert.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50"
                        >
                          {downloadingCert === cert.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Download size={12} />
                          )}
                          <span>Download</span>
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Existing Interview Status */}
          {existingInterview && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={16} className="text-blue-600" />
                <span className="font-medium text-blue-800">Interview Scheduled</span>
                <Badge className={
                  existingInterview.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  existingInterview.status === 'rescheduled' ? 'bg-orange-100 text-orange-800' :
                  'bg-yellow-100 text-yellow-800'
                }>
                  {existingInterview.status === 'confirmed' ? 'Confirmed' :
                   existingInterview.status === 'rescheduled' ? 'Reschedule Requested' :
                   'Pending'}
                </Badge>
              </div>
              <div className="text-sm text-blue-700">
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  {format(toManilaDate(existingInterview.scheduled_at), 'MMMM d, yyyy')} at {format(toManilaDate(existingInterview.scheduled_at), 'h:mm a')}
                </div>
                {existingInterview.status === 'rescheduled' && existingInterview.applicant_response && (
                  <p className="mt-2 text-orange-700 italic">
                    Applicant's message: "{existingInterview.applicant_response}"
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            {!existingInterview ? (
              <Button
                className="flex-1 rounded-xl"
                onClick={() => setShowScheduleModal(true)}
                disabled={checkingInterview}
                data-testid="schedule-interview-button-modal"
              >
                <Calendar size={16} className="mr-2" />
                {checkingInterview ? 'Checking...' : t('interview.scheduleInterview')}
              </Button>
            ) : existingInterview.status === 'rescheduled' ? (
              <Button
                className="flex-1 rounded-xl"
                onClick={() => setShowScheduleModal(true)}
                data-testid="reschedule-interview-button-modal"
              >
                <Calendar size={16} className="mr-2" />
                Schedule New Time
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="flex-1 rounded-xl"
              onClick={() => {
                navigate(`/messages?user=${seeker?.id}`);
                onOpenChange(false);
              }}
              data-testid="send-message-button-modal"
            >
              <MessageCircle size={16} className="mr-2" />
              {t('dashboard.sendMessage')}
            </Button>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => onOpenChange(false)}
            >
              {t('common.close')}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Schedule Interview Modal */}
      <ScheduleInterviewModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        applicant={seeker}
        job={job || applicant?.job ||
          (applicant?.job_id ? { id: applicant.job_id, title: applicant.job_title || 'Job' } : null) ||
          (applicant?.gig_id ? { id: applicant.gig_id, title: applicant.gig_title || 'Gig' } : null)
        }
        applicationId={applicant?.id}
        onSuccess={(newInterview) => {
          setShowScheduleModal(false);
          // Update existing interview state with the new one
          if (newInterview) {
            setExistingInterview(newInterview);
          } else {
            checkExistingInterview();
          }
        }}
      />
    </Dialog>
  );
};

export default ApplicantDetailModal;
